// src/components/trip/PhotoViewer.tsx
import React, { useEffect, useCallback, useState } from 'react';
import { Button, Text } from '@aws-amplify/ui-react';
import { ChevronLeft, ChevronRight, X, Download, Share2 } from 'lucide-react';
import { downloadPhoto } from '../../services/tripPhotoActions';
import { loadPhotoThumbnailUrl, loadPhotoUrl, preloadPhoto } from '../../services/enhancedPhotoUrlService';
import ProgressiveImage from './ProgressiveImage';
import './ProgressiveImage.css';

interface PhotoViewerProps {
  isOpen: boolean;
  photos: any[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (direction: number) => void;
  userRole: string | null; // Add userRole to props
}

const PhotoViewer: React.FC<PhotoViewerProps> = ({
  isOpen,
  photos,
  currentIndex,
  onClose,
  onNavigate,
  userRole
}) => {
  const currentPhoto = photos[currentIndex];
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [videoError, setVideoError] = useState<string | null>(null);
  
  // Check if current file is a video
  const isVideo = currentPhoto?.contentType?.startsWith('video/') || 
                  /\.(mp4|mov|avi|m4v)$/i.test(currentPhoto?.fileName || '');
  
  // Check if user can download/share photos (participants and creators only)
  const canDownload = userRole === 'PARTICIPANT' || userRole === 'CREATOR';
  
  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const mobileCheck = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(mobileCheck);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Add keyboard event listener for navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          onNavigate(-1);
          break;
        case 'ArrowRight':
          onNavigate(1);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, onNavigate]);

  // Reset state when photo changes and load video URL if needed
  useEffect(() => {
    setPhotoUrl(null);
    setIsLoading(false);
    setVideoError(null);
    
    // For videos, load the URL immediately
    if (isVideo && currentPhoto) {
      console.log('Loading video URL for:', currentPhoto.id, currentPhoto.fileName);
      setIsLoading(true);
      loadPhotoUrl(currentPhoto.id, false).then(url => {
        console.log('Video URL loaded:', url);
        setPhotoUrl(url);
        setIsLoading(false);
      }).catch(error => {
        console.error('Error loading video URL:', error);
        setVideoError('Failed to load video');
        setIsLoading(false);
      });
    }
  }, [currentIndex, isVideo, currentPhoto]);

  // Preload adjacent photos
  useEffect(() => {
    if (isOpen && currentPhoto) {
      // Preload next and previous photos
      const nextIndex = (currentIndex + 1) % photos.length;
      const prevIndex = (currentIndex - 1 + photos.length) % photos.length;
      
      // Start preloading
      preloadPhoto(photos[nextIndex].id);
      preloadPhoto(photos[prevIndex].id);
    }
  }, [isOpen, currentIndex, photos]);

  const handleDownload = useCallback(async () => {
    if (!currentPhoto || !canDownload) return;
    
    try {
      setIsLoading(true);
      // If we already have the URL use it, otherwise get the full image URL
      const url = photoUrl || await loadPhotoUrl(currentPhoto.id, false);
      downloadPhoto(currentPhoto.id, url, currentPhoto.fileName);
    } catch (error) {
      console.error('Error downloading photo:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPhoto, photoUrl, canDownload]);

  // Handle sharing for mobile devices
  const handleShare = useCallback(async () => {
    if (!currentPhoto || !canDownload) return;
    
    try {
      setIsLoading(true);
      
      // Get the full URL if not already loaded
      const url = photoUrl || await loadPhotoUrl(currentPhoto.id, false);
      
      if (navigator.share) {
        await navigator.share({
          title: currentPhoto.fileName || 'Shared Photo',
          text: 'Check out this photo',
          url: url
        });
      } else {
        // Fallback to copying to clipboard if Web Share API is not available
        await navigator.clipboard.writeText(url);
        alert('Photo URL copied to clipboard');
      }
    } catch (error) {
      console.error('Error sharing photo:', error);
      // If share fails, try download instead
      handleDownload();
    } finally {
      setIsLoading(false);
    }
  }, [currentPhoto, photoUrl, handleDownload, canDownload]);

  // When full size image/video loads, keep the URL for download
  const handleFullImageLoad = useCallback(async () => {
    if (currentPhoto) {
      try {
        const url = await loadPhotoUrl(currentPhoto.id, false);
        setPhotoUrl(url);
      } catch (error) {
        console.error('Error loading full media URL:', error);
      }
    }
  }, [currentPhoto]);

  if (!isOpen || !currentPhoto) {
    return null;
  }

  return (
    <div className="photo-viewer-overlay" onClick={onClose}>
      <button className="viewer-close-btn" onClick={onClose}>
        <X size={24} />
      </button>

      <div className="viewer-content" onClick={e => e.stopPropagation()}>
        {/* Image/Video container with progressive loading */}
        <div className="viewer-image-container">
          {isVideo ? (
            videoError ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'white',
                padding: '2rem',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>⚠️ Video format not supported in this browser</div>
                <div style={{ fontSize: '0.9rem', color: '#aaa', marginBottom: '1rem' }}>
                  {currentPhoto.fileName.endsWith('.mov') ? 
                    'MOV files have limited browser support. Try downloading the video or use Safari.' :
                    'This video format may not be supported in your browser.'}
                </div>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload();
                  }}
                  style={{ marginTop: '1rem' }}
                >
                  Download Video
                </Button>
              </div>
            ) : photoUrl ? (
              <video
                key={currentPhoto.id}
                controls
                playsInline
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  backgroundColor: '#000'
                }}
                onLoadedData={handleFullImageLoad}
                onError={(e) => {
                  console.error('Video playback error:', e);
                  setVideoError('Video format not supported');
                }}
              >
                <source src={photoUrl} type={currentPhoto.contentType || 'video/mp4'} />
                {/* Fallback for browsers that might need different type hints */}
                {currentPhoto.fileName.endsWith('.mov') && (
                  <source src={photoUrl} type="video/quicktime" />
                )}
                Your browser does not support this video format.
              </video>
            ) : (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'white'
              }}>
                <div>Loading video...</div>
              </div>
            )
          ) : (
            <ProgressiveImage
              photoId={currentPhoto.id}
              alt={currentPhoto.fileName}
              objectFit="contain"
              width="100%"
              height="100%"
              loadFullImage={true}
              useBlurTransition={true} // Enable blur transition for viewer
              onLoad={handleFullImageLoad}
            />
          )}

          <button
            className="viewer-nav-btn prev-btn"
            onClick={(e) => { e.stopPropagation(); onNavigate(-1); }}
            aria-label="Previous photo"
          >
            <ChevronLeft size={24} />
          </button>

          <button
            className="viewer-nav-btn next-btn"
            onClick={(e) => { e.stopPropagation(); onNavigate(1); }}
            aria-label="Next photo"
          >
            <ChevronRight size={24} />
          </button>
        </div>

        {/* Photo information bar - below the image */}
        <div className="viewer-info-bar">
          <div className="viewer-counter">
            {currentIndex + 1} / {photos.length}
          </div>

          {/* Only show download/share actions if user has permission */}
          {canDownload && (
            <div className="viewer-actions">
              {isMobile ? (
                // Show share button on mobile devices
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleShare();
                  }}
                  aria-label="Share photo"
                  className="viewer-action-btn"
                  isLoading={isLoading}
                >
                  <Share2 size={18} />
                </Button>
              ) : (
                // Show download button on desktop
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload();
                  }}
                  aria-label="Download photo"
                  className="viewer-action-btn"
                  isLoading={isLoading}
                >
                  <Download size={18} />
                </Button>
              )}
            </div>
          )}

          <div className="viewer-caption">
            <Text fontWeight="medium">{currentPhoto.uploaderName || 'Unknown'}</Text>
            <Text fontSize="0.9rem" color="gray">
              {new Date(currentPhoto.createdAt).toLocaleString()}
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoViewer;